import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const CreateSSP = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    template_id: '',
    metadata: {
      isso_name: '',
      issm_name: '',
      system_name: '',
      system_description: '',
      system_owner: '',
      hosting_model: '',
      authorization_boundary: '',
      impact_level: ''
    }
  });

  useEffect(() => {
    fetchPublishedTemplates();
  }, []);

  const fetchPublishedTemplates = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/templates/published`, {
        withCredentials: true
      });
      setTemplates(response.data.data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('metadata.')) {
      const metadataField = name.split('.')[1];
      setFormData({
        ...formData,
        metadata: {
          ...formData.metadata,
          [metadataField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.post(
        `${baseURL}/ssp-studio/ssps`,
        formData,
        { withCredentials: true }
      );

      // Navigate to the newly created SSP
      navigate(`/ssp-studio/ssp/${response.data.data._id}`);
    } catch (err) {
      console.error('Error creating SSP:', err);
      setError(err.response?.data?.message || 'Failed to create SSP. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col lg={8} className="mx-auto">
          <Card>
            <CardBody>
              <CardTitle className="mb-4">
                <i className="mdi mdi-file-document-plus text-primary me-2"></i>
                Create New System Security Plan
              </CardTitle>

              {error && (
                <Alert color="danger" toggle={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="name">SSP Name *</Label>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        placeholder="e.g., Production Environment SSP"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="template_id">Select Template *</Label>
                      <Input
                        type="select"
                        name="template_id"
                        id="template_id"
                        value={formData.template_id}
                        onChange={handleChange}
                        required
                        disabled={templates.length === 0}
                      >
                        <option value="">-- Choose a template --</option>
                        {templates.map((template) => (
                          <option key={template._id} value={template._id}>
                            {template.name} (v{template.version}) - {template.sections?.length || 0} sections
                          </option>
                        ))}
                      </Input>
                      {templates.length === 0 && (
                        <Alert color="warning" className="mt-2">
                          <i className="mdi mdi-alert me-2"></i>
                          No published templates available. Please go to the <strong>Templates</strong> tab and create/publish a template first.
                        </Alert>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">System Metadata</h6>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="system_name">System Name</Label>
                      <Input
                        type="text"
                        name="metadata.system_name"
                        id="system_name"
                        placeholder="Official system name"
                        value={formData.metadata.system_name}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="system_owner">System Owner</Label>
                      <Input
                        type="text"
                        name="metadata.system_owner"
                        id="system_owner"
                        placeholder="Name of system owner"
                        value={formData.metadata.system_owner}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="isso_name">ISSO Name</Label>
                      <Input
                        type="text"
                        name="metadata.isso_name"
                        id="isso_name"
                        placeholder="Information System Security Officer"
                        value={formData.metadata.isso_name}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="issm_name">ISSM Name</Label>
                      <Input
                        type="text"
                        name="metadata.issm_name"
                        id="issm_name"
                        placeholder="Information System Security Manager"
                        value={formData.metadata.issm_name}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="hosting_model">Hosting Model</Label>
                      <Input
                        type="select"
                        name="metadata.hosting_model"
                        id="hosting_model"
                        value={formData.metadata.hosting_model}
                        onChange={handleChange}
                      >
                        <option value="">-- Select --</option>
                        <option value="Cloud">Cloud</option>
                        <option value="On-Premise">On-Premise</option>
                        <option value="Hybrid">Hybrid</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="impact_level">Impact Level</Label>
                      <Input
                        type="select"
                        name="metadata.impact_level"
                        id="impact_level"
                        value={formData.metadata.impact_level}
                        onChange={handleChange}
                      >
                        <option value="">-- Select --</option>
                        <option value="Low">Low</option>
                        <option value="Moderate">Moderate</option>
                        <option value="High">High</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="authorization_boundary">Authorization Boundary</Label>
                      <Input
                        type="textarea"
                        name="metadata.authorization_boundary"
                        id="authorization_boundary"
                        rows="3"
                        placeholder="Describe the authorization boundary"
                        value={formData.metadata.authorization_boundary}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md={12}>
                    <FormGroup>
                      <Label for="system_description">System Description</Label>
                      <Input
                        type="textarea"
                        name="metadata.system_description"
                        id="system_description"
                        rows="4"
                        placeholder="Brief description of the system"
                        value={formData.metadata.system_description}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="mt-4 d-flex justify-content-between">
                  <Button type="button" color="secondary" outline onClick={() => navigate('/ssp-studio/ssps')}>
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" disabled={loading || templates.length === 0}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-check me-1"></i>
                        Create SSP
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateSSP;
